import { useState } from 'react'

import { BUSINESS_TIMEZONE } from '../../lib/datetime'
import { laborFrom, materialPrice, priceAsOf, qtyNote, quantity } from '../../lib/pricing'
import type { Cart, CartLine } from '../../lib/wstypes'

/**
 * The list of what the homeowner has chosen (01 §3.5 "Cart", §7).
 *
 * Called a **list**, not a cart, everywhere the homeowner can see — nothing here is being
 * bought and the word implies a checkout that does not exist.
 *
 * There is no total. Not hidden, not collapsed: absent. 01 §7 makes that a hard requirement,
 * `CartLine` has no field that could hold one, and the disclaimer under the lines says what
 * happens instead — Andrew confirms on site and sends the price.
 */

interface Props {
  cart: Cart
  open: boolean
  onClose: () => void
  onQty: (lineId: string, qty: number) => void
  onRemove: (lineId: string) => void
  onSend: () => void
  onDeleteMyData: () => void
  sending?: boolean
  readOnly?: boolean
}

export function CartSheet({
  cart,
  open,
  onClose,
  onQty,
  onRemove,
  onSend,
  onDeleteMyData,
  sending,
  readOnly,
}: Props) {
  if (!open) return null
  const byRoom = groupByRoom(cart.lines)

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-navy/40 sm:items-end sm:justify-center sm:p-6">
      <button
        type="button"
        aria-label="Close the list"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-heading"
        className="relative flex max-h-[86vh] flex-col rounded-t-[20px] bg-paper shadow-lift sm:max-h-[80vh] sm:w-[440px] sm:rounded-card"
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h2 id="cart-heading" className="text-xl">
            Your list
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 min-w-11 rounded-btn border border-line bg-white px-3 text-sm font-semibold text-ink"
          >
            Done
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {cart.lines.length === 0 && (
            <p className="m-0 text-muted">
              Nothing on the list yet. Tap something in your photo, pick a product, and it lands
              here.
            </p>
          )}

          {[...byRoom.entries()].map(([room, lines]) => (
            <section key={room} className="mb-6">
              <h3 className="font-mono text-xs uppercase tracking-[0.14em] text-muted">{room}</h3>
              <ul className="m-0 mt-2 grid list-none gap-2.5 p-0">
                {lines.map((line) => (
                  <li key={line.id}>
                    <Line
                      line={line}
                      onQty={(qty) => onQty(line.id, qty)}
                      onRemove={() => onRemove(line.id)}
                      readOnly={readOnly}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="border-t border-line bg-white px-5 py-4">
          {/* 01 §7, verbatim from the server so one sentence governs the UI, the email and
              the PDF. */}
          <p className="m-0 text-xs leading-relaxed text-muted">{cart.disclaimer}</p>
          <button
            type="button"
            onClick={onSend}
            disabled={cart.lines.length === 0 || sending || readOnly}
            className="mt-3 min-h-12 w-full rounded-btn bg-navy px-5 font-semibold text-white disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send to Andrew'}
          </button>

          {/* 08 §2 gives a person the right to have their data deleted. It belongs where they
              are already looking at what we hold, not on a policy page they have to find. */}
          <button
            type="button"
            onClick={onDeleteMyData}
            className="mt-3 min-h-11 w-full text-xs text-muted underline underline-offset-2 hover:text-ink"
          >
            Delete my data
          </button>
        </footer>
      </section>
    </div>
  )
}

function Line({
  line,
  onQty,
  onRemove,
  readOnly,
}: {
  line: CartLine
  onQty: (qty: number) => void
  onRemove: () => void
  readOnly?: boolean
}) {
  const [draft, setDraft] = useState(line.qty)
  const note = qtyNote(line)

  return (
    <article className="rounded-card border border-line bg-white p-3">
      <div className="flex gap-3">
        {line.image_url ? (
          <img src={line.image_url} alt="" className="size-16 shrink-0 rounded-btn object-cover" />
        ) : (
          <div className="grid size-16 shrink-0 place-items-center rounded-btn bg-mist text-xs text-muted">
            Custom
          </div>
        )}
        <div className="min-w-0 flex-1">
          {line.slot_name && (
            <p className="m-0 font-mono text-xs uppercase tracking-[0.1em] text-muted">
              {line.slot_name}
            </p>
          )}
          <h4 className="m-0 text-sm font-semibold leading-snug text-ink">
            {line.product_name ?? line.custom_description ?? 'Custom work'}
          </h4>

          {line.material ? (
            <>
              <p className="m-0 mt-1 text-sm text-ink">{materialPrice(line.material)}</p>
              <p className="m-0 text-xs text-muted">
                {line.attribution?.store} price as of{' '}
                {priceAsOf(line.material_price_as_of, BUSINESS_TIMEZONE)}
              </p>
            </>
          ) : (
            <p className="m-0 mt-1 text-sm text-ink">Custom — estimated on site</p>
          )}
          <p className="m-0 text-xs text-body">{laborFrom(line.labor_from)}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-muted">
          <span>Qty</span>
          <input
            type="number"
            inputMode="decimal"
            min="0.1"
            step="0.1"
            value={draft}
            disabled={readOnly}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              const value = Number(draft)
              if (value > 0 && value !== Number(line.qty)) onQty(value)
            }}
            className="min-h-11 w-20 rounded-btn border border-line bg-white px-2 text-base text-ink"
          />
          <span className="text-ink">{quantity(line).split(' ').slice(1).join(' ')}</span>
        </label>
        <button
          type="button"
          onClick={onRemove}
          disabled={readOnly}
          className="ml-auto min-h-11 rounded-btn border border-line bg-white px-3 text-xs font-semibold text-blue-press disabled:opacity-50"
        >
          Remove
        </button>
      </div>

      {note && <p className="m-0 mt-2 text-xs italic text-muted">{note}</p>}
    </article>
  )
}

function groupByRoom(lines: CartLine[]): Map<string, CartLine[]> {
  const map = new Map<string, CartLine[]>()
  for (const line of lines) {
    const key = line.room_name ?? 'Other'
    map.set(key, [...(map.get(key) ?? []), line])
  }
  return map
}
